import { CommonModule } from '@angular/common';
import { ETomKpi, KpiComissoesComponent, SEM_DADO_KPI } from '@/features/comissoes/dashboard-comissoes/components/kpi-comissoes/kpi-comissoes';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, OnChanges, OnInit, SimpleChanges, AfterViewInit, inject, input, output, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, of } from 'rxjs';
import { Popover } from 'primeng/popover';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { FormsModule } from '@angular/forms';
import { MenuItem } from 'primeng/api';
import { TablePageEvent } from 'primeng/table';
import { ReuniaoComissao, StatusReuniaoComissao } from '@/features/comissoes/dashboard-comissoes/comissoes-dashboard.model';
import { ComissaoReuniaoDashboardSummaryResponse } from '@/core/api/sicom-api/comissoes/reunioes/comissao-reuniao.model';
import { ComissaoReuniaoService } from '@/core/api/sicom-api/comissoes/reunioes/comissao-reuniao.service';
import { BotaoAcoes, EInconPos } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { CustomTableComponent, TableMetadata, TablePerfilValue } from '@/shared/components/layout/custom-table/custom-table.component';
import { DetalheGrid } from '@/shared/components/layout/detalhe-grid/detalhe-grid';
import { DetalheItem } from '@/shared/components/layout/detalhe-grid/detalhe-item';
import { construirMetadataReunioes } from './tabela-reunioes-comissoes.config';
import { AvatarModule } from 'primeng/avatar';
import { CardContainer } from '@/shared/components/card-container/card-container';
import { CardContent } from '@/shared/components/card-content/card-content';
import { SendWhatsapp } from '@/shared/components/send-whatsapp/send-whatsapp';
import { AppMenuItem } from '@/core/permissions/permission.model';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

export type AcaoTabelaComissoes =
    | 'abrir-ata'
    | 'editar'
    | 'cancelar'
    | 'excluir'
    | 'convocacoes'
    | 'ir-para-atividades'
    | 'visualizar-comissao-nucleo'
    | 'nova-atividade-reuniao'
    | 'reativar-reuniao'
    | 'status-ata-anterior'
    | 'status-ata-proximo'
    | 'visualizar-selecionada'
    | 'whatsapp';

/** Severidades do PrimeNG usadas pelas etiquetas e pelos pictogramas desta tabela. */
export type SeveridadeReuniao = 'success' | 'warn' | 'danger' | 'secondary' | 'contrast' | 'info';

/** Valor exibível junto da cor que o representa; `icon` só existe onde o dado virou pictograma. */
interface ValorSinalizado {
    value: string;
    severity?: SeveridadeReuniao;
    color?: string;
    icon?: string;
}

/** Um pictograma da legenda: o mesmo ícone e o mesmo tom que a célula usa. */
export interface ItemLegenda {
    icone: string;
    tom: SeveridadeReuniao | 'tinta';
    nome: string;
}

/** Um assunto da legenda — espelha os grupos que a célula separa com o filete. */
export interface BlocoLegenda {
    titulo: string;
    itens: ItemLegenda[];
    nota?: string;
}

export interface ReuniaoRowItem {
    codigo: number;
    status: ValorSinalizado;
    visivelPortal: ValorSinalizado;
    modalidade: ValorSinalizado;
    atividades: ValorSinalizado;
    codigoComissao: string;
    nomeComissao: string;
    statusAta: ValorSinalizado;
    local: ValorSinalizado;
    dataHoraCadastro: string;
    dataInicio: string;
    dataTermino: string;
    assistente: string;
    convocacoes: string;
    confirmacoes: string;
    atosConcessao: string;
    importante: ValorSinalizado;
    protocolo: string;
    source: ReuniaoComissao;
    /** Derivados para a renderização genérica da tabela — ver `atualizarDadosMapeados`. */
    codigoExibicao?: string;
    protocoloChip?: ValorSinalizado;
    diaCadastro?: string;
    horaCadastro?: string;
    diaInicio?: string;
    faixaHorarioTexto?: string;
    participacao?: string;
    assistentePerfil?: TablePerfilValue;
    localTexto?: string;
    tituloComissao?: string;
}

@Component({
    selector: 'app-tabela-reunioes-comissoes',
    standalone: true,
    imports: [CommonModule, CustomTableComponent, BotaoAcoes, CardContainer, Popover, ButtonModule, TooltipModule, FormsModule, CardContent, SendWhatsapp, AvatarModule, DetalheGrid, DetalheItem, KpiComissoesComponent],
    templateUrl: './tabela-reunioes-comissoes.html',
    styleUrls: ['./tabela-reunioes-comissoes.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
/**
 * Tabela de reuniões da comissão (custom-table + visão card): seleção de linha, ações da reunião
 * (ata/editar/cancelar/convocações/status…) no menu "Opções", indicadores e colunas configuráveis.
 */
export class TabelaReunioesComissoesComponent implements OnChanges, OnInit, AfterViewInit {
    protected readonly EInconPos = EInconPos;
    protected readonly linhasPorPaginaPadrao = 20;
    protected readonly opcoesLinhasPorPagina = [10, 20, 50, 100];
    protected readonly itensAcoesTabelaMenuBase: MenuItem[] = [
        {
            label: 'Abrir Atas',
            icon: 'pi pi-file-edit',
            styleClass: 'bg-indigo-500',
            command: () => this.emitirAcao('abrir-ata')
        },
        {
            label: 'Editar',
            icon: 'pi pi-pencil',
            styleClass: 'bg-blue-500',
            command: () => this.emitirAcao('editar')
        },
        {
            label: 'Cancelar',
            icon: 'pi pi-ban',
            styleClass: 'bg-orange-500',
            command: () => this.emitirAcao('cancelar')
        },
        {
            label: 'Excluir',
            icon: 'pi pi-trash',
            styleClass: 'bg-red-500',
            command: () => this.emitirAcao('excluir')
        },
        {
            label: 'Convocações',
            icon: 'pi pi-users',
            styleClass: 'bg-cyan-500',
            command: () => this.emitirAcao('convocacoes')
        },
        {
            label: 'Ir para Atividades',
            icon: 'pi pi-arrow-right',
            styleClass: 'bg-blue-500',
            command: () => this.emitirAcao('ir-para-atividades')
        },
        {
            label: 'Visualizar Comissão / Núcleo',
            icon: 'pi pi-eye',
            styleClass: 'bg-surface-700',
            command: () => this.emitirAcao('visualizar-comissao-nucleo')
        },
        {
            label: 'Nova atividade',
            icon: 'pi pi-plus-circle',
            styleClass: 'bg-surface-500',
            command: () => this.emitirAcao('nova-atividade-reuniao')
        },
        {
            label: 'Status anterior',
            icon: 'pi pi-arrow-left',
            styleClass: 'bg-amber-500',
            command: () => this.emitirAcao('status-ata-anterior')
        },
        {
            label: 'Próximo status',
            icon: 'pi pi-arrow-right',
            styleClass: 'bg-emerald-500',
            command: () => this.emitirAcao('status-ata-proximo')
        },
        {
            separator: true
        },
        {
            label: 'Reativar',
            icon: 'pi pi-refresh',
            styleClass: 'bg-surface-500',
            command: () => this.emitirAcao('reativar-reuniao')
        }
    ];

    private readonly comissaoReuniaoService = inject(ComissaoReuniaoService);
    private readonly destroyRef = inject(DestroyRef);
    /** OnPush: `dataMapped` é propriedade comum, então a foto que chega fora de um input precisa avisar. */
    private readonly cdr = inject(ChangeDetectorRef);

    protected readonly fotosAssistentes = signal<ReadonlyMap<string, string | null>>(new Map());

    /** Referência à custom-table para sincronizar a linha selecionada. */
    readonly customTable = viewChild(CustomTableComponent);
    /** Componente de envio de WhatsApp (disparado pela ação "Compartilhar"). */
    readonly sendWhatsappComponent = viewChild<SendWhatsapp>('sendWhatsapp');

    /**
     * Seis colunas compostas, cada uma empilhando linhas do mesmo assunto. A chave de cada coluna é o
     * campo principal que ela mostra, para exportação e ordenação continuarem apontando para um dado
     * real. Declaração completa em `tabela-reunioes-comissoes.config.ts`.
     */
    metadataTabela: TableMetadata = construirMetadataReunioes();

    /**
     * A legenda começa fechada: são 171px medidos antes da primeira linha, altura gasta para
     * quem já conhece os pictogramas. A tela abre na lista; quem precisa da legenda chama pelo
     * botão de informação no cabeçalho. O estado é local e não persiste entre navegações.
     */
    protected readonly legendaVisivel = signal(false);

    protected alternarLegenda(): void {
        this.legendaVisivel.update((visivel) => !visivel);
    }

    protected readonly blocosLegenda: BlocoLegenda[] = [
        {
            titulo: 'Reunião',
            itens: [
                { icone: 'pi pi-calendar', tom: 'info', nome: 'Agendada' },
                { icone: 'pi pi-refresh', tom: 'warn', nome: 'Remarcada' },
                { icone: 'pi pi-check-circle', tom: 'success', nome: 'Realizada' },
                { icone: 'pi pi-ban', tom: 'danger', nome: 'Cancelada' },
                { icone: 'pi pi-trash', tom: 'danger', nome: 'Excluída' }
            ]
        },
        {
            titulo: 'Ata',
            itens: [
                { icone: 'pi pi-file-edit', tom: 'warn', nome: 'Minuta pendente' },
                { icone: 'pi pi-search', tom: 'danger', nome: 'Revisão pendente' },
                { icone: 'pi pi-pencil', tom: 'warn', nome: 'Assinatura pendente' },
                { icone: 'pi pi-file-check', tom: 'success', nome: 'Concluída' },
                { icone: 'pi pi-megaphone', tom: 'info', nome: 'Publicada' },
                { icone: 'pi pi-file', tom: 'secondary', nome: 'Sem ata' }
            ]
        },
        {
            titulo: 'Marcas',
            itens: [
                { icone: 'pi pi-flag-fill', tom: 'warn', nome: 'Ata importante' },
                { icone: 'pi pi-globe', tom: 'tinta', nome: 'Visível no portal' },
                { icone: 'pi pi-list-check', tom: 'tinta', nome: 'Com atividades' }
            ],
            nota: 'Ícone apagado significa não.'
        },
        {
            titulo: 'Participação',
            itens: [
                { icone: 'pi pi-users', tom: 'tinta', nome: 'Confirmadas / convocadas' },
                { icone: 'pi pi-verified', tom: 'tinta', nome: 'Atos de concessão' }
            ]
        }
    ];

    dataMapped: ReuniaoRowItem[] = [];

    readonly reunioes = input<ReuniaoComissao[]>([]);
    /** Resumo do dashboard (indicadores de próxima/última reunião). */
    readonly dashboardSummary = input<ComissaoReuniaoDashboardSummaryResponse | null>(null);
    readonly totalRegistros = input<number | null>(null);

    /** Índice do primeiro registro (paginação). */
    readonly first = input(0);
    readonly rows = input(this.linhasPorPaginaPadrao);
    readonly reuniaoSelecionada = input<ReuniaoComissao | null>(null);
    readonly carregando = input(false);
    readonly itensOpcoes = input<MenuItem[]>([]);
    readonly filtrosVisiveis = input(true);
    readonly selecionarReuniao = output<ReuniaoComissao | null>();
    readonly acaoTabela = output<AcaoTabelaComissoes>();
    readonly paginaAlterada = output<TablePageEvent>();
    readonly alternarFiltros = output<void>();

    opcoesColunas: { label: string; value: string }[] = [];
    colunasSelecionadas: string[] = [];

    ngOnInit(): void {
        this.inicializarColunas();
    }

    /**
     * O seletor lista as colunas da tabela, não os pictogramas: as marcas recolhidas em `resumo`
     * dividem uma célula só ("Informações"), então oferecê-las uma a uma trocaria seis opções por
     * doze para controlar o mesmo espaço. Elas seguem as demais — se a célula precisar sumir um
     * dia, o lugar da opção é "Informações", não cada marca.
     */
    inicializarColunas(): void {
        const colunas = Object.entries(this.metadataTabela.headers).filter(([, header]) => !header.resumo);

        this.opcoesColunas = colunas.map(([key, header]) => ({
            label: header.value,
            value: key
        }));
        this.colunasSelecionadas = colunas.filter(([, header]) => !header.hidden).map(([key]) => key);
    }

    atualizarColunasVisiveis(): void {
        const novosHeaders = { ...this.metadataTabela.headers };
        Object.keys(novosHeaders).forEach((key) => {
            // Pictogramas do resumo não passam pelo seletor — ocultá-los aqui apagaria a célula
            // "Informações" inteira sem que ninguém tivesse pedido.
            if (novosHeaders[key].resumo) return;
            novosHeaders[key] = {
                ...novosHeaders[key],
                hidden: !this.colunasSelecionadas.includes(key)
            };
        });
        this.metadataTabela = {
            ...this.metadataTabela,
            headers: novosHeaders
        };
    }

    todasSelecionadas(): boolean {
        return this.colunasSelecionadas.length === this.opcoesColunas.length;
    }

    toggleTodasColunas(): void {
        if (this.todasSelecionadas()) {
            this.colunasSelecionadas = [];
        } else {
            this.colunasSelecionadas = this.opcoesColunas.map((o) => o.value);
        }
        this.atualizarColunasVisiveis();
    }

    toggleColuna(colKey: string): void {
        if (this.colunasSelecionadas.includes(colKey)) {
            this.colunasSelecionadas = this.colunasSelecionadas.filter((c) => c !== colKey);
        } else {
            this.colunasSelecionadas = [...this.colunasSelecionadas, colKey];
        }
        this.atualizarColunasVisiveis();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['reunioes']) {
            this.atualizarDadosMapeados();
        }
        if (changes['reuniaoSelecionada']) {
            this.sincronizarSelecao();
        }
    }

    ngAfterViewInit(): void {
        this.sincronizarSelecao();
    }

    private atualizarDadosMapeados(): void {
        this.dataMapped = (this.reunioes() || []).map((reuniao) => ({
            codigo: Number(reuniao.codigo),
            status: {
                value: this.statusRotulo(reuniao.status),
                severity: this.statusSeveridade(reuniao.status),
                icon: this.obterIconeStatus(this.statusRotulo(reuniao.status))
            },
            visivelPortal: {
                value: reuniao.visivelPortal ? 'Sim' : 'Não',
                severity: reuniao.visivelPortal ? 'success' : 'danger'
            },
            modalidade: {
                value: reuniao.modalidade,
                severity: this.modalidadeSeveridade(reuniao.modalidade)
            },
            atividades: {
                value: reuniao.atividades === 'Sim' || reuniao.atividades === 'Sim' ? 'Sim' : 'Não',
                severity: reuniao.atividades === 'Sim' ? 'success' : 'danger'
            },
            codigoComissao: reuniao.codigoComissao,
            nomeComissao: reuniao.nomeComissao,
            statusAta: {
                value: this.statusAtaRotulo(reuniao.statusAta),
                severity: this.statusAtaSeveridade(reuniao.statusAta),
                icon: this.obterIconeStatusAta(reuniao.statusAta)
            },
            local: {
                value: reuniao.local || '-',
                severity: this.localSeveridade(reuniao.local || '')
            },
            dataHoraCadastro: this.formatarData(reuniao.dataHoraCadastro),
            dataInicio: this.formatarData(reuniao.dataInicio),
            dataTermino: this.formatarData(reuniao.dataTermino),
            assistente: reuniao.assistente,
            convocacoes: String(reuniao.convocacoes),
            confirmacoes: String(reuniao.confirmacoes),
            atosConcessao: String(reuniao.atosConcessao),
            importante: this.marcacaoImportante(reuniao.importante),
            protocolo: reuniao.protocolo || '-',
            source: reuniao,
            // Campos derivados só para a tabela: a renderização genérica lê valores prontos da
            // linha, não chama método do componente como o `pTemplate="cell"` fazia.
            codigoExibicao: `#${reuniao.codigo}`,
            protocoloChip: { value: reuniao.protocolo || '-', color: 'var(--p-violet-500, #8b5cf6)' },
            diaCadastro: this.diaDe(this.formatarData(reuniao.dataHoraCadastro)),
            horaCadastro: this.horaDe(this.formatarData(reuniao.dataHoraCadastro)),
            diaInicio: this.diaDe(this.formatarData(reuniao.dataInicio)),
            localTexto: reuniao.local || '-'
        }));

        // Faixa de horário e razão de participação dependem da linha inteira já formatada —
        // calculadas num segundo passo. A razão passa por `razaoParticipacao`, e não por uma
        // concatenação aqui: sem convocação a razão não existe ("12/0" leria como dado corrompido)
        // e sobra só quantas confirmaram.
        this.dataMapped = this.dataMapped.map((item) => ({
            ...item,
            faixaHorarioTexto: this.faixaHorario(item),
            participacao: this.razaoParticipacao(item),
            assistentePerfil: this.perfilAssistente(item),
            tituloComissao: this.tituloComissao(item)
        }));
        this.sincronizarSelecao();
        this.carregarFotosAssistentes();
    }

    /** Busca as fotos dos assistentes da página que ainda não estão no cache. */
    private carregarFotosAssistentes(): void {
        const pendentes = new Map<string, { id: number; uf?: string }>();
        for (const reuniao of this.reunioes() || []) {
            const id = reuniao.idSolicitanteAssistente;
            if (!id) continue;
            const chave = this.chaveFotoAssistente(id, reuniao.ufSolicitanteAssistente);
            if (!this.fotosAssistentes().has(chave) && !pendentes.has(chave)) {
                pendentes.set(chave, { id, uf: reuniao.ufSolicitanteAssistente ?? undefined });
            }
        }

        pendentes.forEach((alvo, chave) => {
            this.comissaoReuniaoService
                .obterFotoAssistente(alvo.id, alvo.uf)
                .pipe(
                    // Foto é enfeite: falha vira "sem foto" (iniciais), nunca erro na tela.
                    catchError(() => of({ foto: null })),
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe((resposta) => {
                    const mapa = new Map(this.fotosAssistentes());
                    mapa.set(chave, resposta.foto ?? null);
                    this.fotosAssistentes.set(mapa);
                    // A coluna `perfil` lê um valor pronto da linha, não chama método do componente
                    // como o template fazia — então a foto que chega depois precisa ser reescrita
                    // nas linhas. Sem isto o retrato só apareceria na próxima troca de página.
                    this.reescreverPerfilAssistente();
                });
        });
    }

    private chaveFotoAssistente(id: number, uf?: string | null): string {
        return `${id}|${uf ?? ''}`;
    }

    /**
     * Retrato + nome para a coluna `perfil`. Assistente ausente vira nome vazio, e a coluna
     * mostra o `default` do metadata ("Não Informado") em meia-tinta.
     */
    private perfilAssistente(item: ReuniaoRowItem): TablePerfilValue {
        const iniciais = this.iniciaisAssistente(item.assistente);
        if (!iniciais) {
            return { nome: '', foto: null, iniciais: null };
        }
        return { nome: item.assistente, foto: this.fotoAssistente(item), iniciais };
    }

    /** Reprojeta só o perfil das linhas — usado quando uma foto chega depois da montagem. */
    private reescreverPerfilAssistente(): void {
        this.dataMapped = this.dataMapped.map((item) => ({ ...item, assistentePerfil: this.perfilAssistente(item) }));
        this.cdr.markForCheck();
    }

    /** Foto do assistente da linha, se o backend a devolveu; nulo cai para as iniciais. */
    protected fotoAssistente(item: ReuniaoRowItem): string | null {
        const id = item.source.idSolicitanteAssistente;
        if (!id) return null;
        return this.fotosAssistentes().get(this.chaveFotoAssistente(id, item.source.ufSolicitanteAssistente)) ?? null;
    }

    private sincronizarSelecao(): void {
        const tabela = this.customTable();
        if (!tabela) return;

        if (!this.reuniaoSelecionada()) {
            tabela.selectedRow.set(null);
            return;
        }

        const itemMapeado = this.dataMapped.find((item) => item.codigo === Number(this.reuniaoSelecionada()?.codigo));
        tabela.selectedRow.set(itemMapeado || null);
    }

    /**
     * O backend entrega o flag cru ('S'/'N', nulo quando a reunião não tem ata). A coluna
     * exibia esse valor direto, destoando de "Visível Portal" e "Atividades" na mesma tabela.
     */
    private marcacaoImportante(valor?: string | null): ReuniaoRowItem['importante'] {
        const flag = (valor || '').trim().toUpperCase();
        if (flag === 'S') return { value: 'Sim', severity: 'success' };
        if (flag === 'N') return { value: 'Não', severity: 'danger' };
        return { value: '-', severity: 'secondary' };
    }

    private formatarData(valor?: Date | string | null): string {
        if (!valor) return '-';
        try {
            const d = typeof valor === 'string' ? new Date(valor) : valor;
            if (isNaN(d.getTime())) return '-';
            const dd = String(d.getDate()).padStart(2, '0');
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const yyyy = d.getFullYear();
            const hh = String(d.getHours()).padStart(2, '0');
            const min = String(d.getMinutes()).padStart(2, '0');
            return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
        } catch {
            return String(valor);
        }
    }

    handleRowClick(rowItem: ReuniaoRowItem | null): void {
        this.onSelecaoAlterada(rowItem ? rowItem.source : null);
    }

    onSelecaoAlterada(valor: ReuniaoComissao | null): void {
        this.selecionarReuniao.emit(valor);
    }

    onVisualizarClicado(reuniao: ReuniaoComissao): void {
        this.selecionarReuniao.emit(reuniao);
        this.emitirAcao('visualizar-selecionada');
    }

    onPaginaAlterada(event: TablePageEvent): void {
        this.paginaAlterada.emit(event);
    }

    get totalReunioes(): number {
        return this.totalRegistros() ?? (this.reunioes() || []).length;
    }

    get totalAgendadas(): number {
        return (this.reunioes() || []).filter((r) => r.status === 'agendada' || r.status === 'remarcada').length;
    }

    get totalRealizadas(): number {
        return (this.reunioes() || []).filter((r) => r.status === 'realizada').length;
    }

    get totalCanceladas(): number {
        return (this.reunioes() || []).filter((r) => r.status === 'cancelada' || r.status === 'excluida').length;
    }

    protected itensAcoesTabelaMenu(): AppMenuItem[] {
        const reuniao = this.reuniaoSelecionada();
        const idStatusReuniao = this.idStatusReuniao(reuniao);
        const possuiReuniao = Boolean(reuniao);
        const permiteAtaEStatus = possuiReuniao && this.statusPermiteAtaEStatus(idStatusReuniao);
        const permiteNovaAtividade = possuiReuniao && this.statusPermiteNovaAtividade(idStatusReuniao);
        const permiteReativar = possuiReuniao && idStatusReuniao === 5;
        // Ata publicada (5) é irreversível: não oferecer "Status anterior" (o backend também rejeita a regressão).
        const podeExibirStatusAnterior = Boolean(reuniao?.idStatusAnterior && reuniao.idStatusAnterior > 0 && idStatusReuniao !== 3 && reuniao?.idStatusAta !== 5);
        const podeExibirProximoStatus = Boolean(reuniao?.idProximoStatus && reuniao.idProximoStatus > 0 && idStatusReuniao !== 3);

        return [
            {
                label: 'Abrir Ata',
                icon: 'pi pi-file-edit',
                styleClass: 'bg-indigo-500',
                disabled: !permiteAtaEStatus,
                permission: PERMISSOES.comissoes.atas.gerarProtocolo,
                command: () => this.emitirAcao('abrir-ata')
            },
            {
                label: 'Editar',
                icon: 'pi pi-pencil',
                styleClass: 'bg-blue-500',
                disabled: !possuiReuniao,
                permission: PERMISSOES.comissoes.reunioes.editar,
                command: () => this.emitirAcao('editar')
            },
            {
                label: 'Cancelar',
                icon: 'pi pi-ban',
                styleClass: 'bg-orange-500',
                disabled: !possuiReuniao,
                permission: PERMISSOES.comissoes.reunioes.cancelar,
                command: () => this.emitirAcao('cancelar')
            },
            {
                label: 'Excluir',
                icon: 'pi pi-trash',
                styleClass: 'bg-red-500',
                disabled: !possuiReuniao,
                permission: PERMISSOES.comissoes.reunioes.excluir,
                command: () => this.emitirAcao('excluir')
            },
            {
                label: 'Convocações',
                icon: 'pi pi-users',
                styleClass: 'bg-cyan-500',
                disabled: !possuiReuniao,
                permission: PERMISSOES.comissoes.convocacoes.visualizar,
                command: () => this.emitirAcao('convocacoes')
            },
            {
                label: 'Compartilhar',
                icon: 'pi pi-whatsapp',
                styleClass: 'bg-green-500',
                disabled: !possuiReuniao,
                command: () => this.executarAcaoWhatsapp()
            },
            {
                label: 'Atividades',
                icon: 'pi pi-arrow-right',
                styleClass: 'bg-blue-500',
                disabled: !(possuiReuniao && reuniao?.atividades === 'Sim'),
                command: () => this.emitirAcao('ir-para-atividades')
            },
            {
                label: 'Comissão / Núcleo',
                icon: 'pi pi-eye',
                styleClass: 'bg-surface-700',
                disabled: !possuiReuniao,
                command: () => this.emitirAcao('visualizar-comissao-nucleo')
            },
            {
                label: 'Nova atividade',
                icon: 'pi pi-plus-circle',
                styleClass: 'bg-surface-500',
                disabled: !permiteNovaAtividade,
                permission: PERMISSOES.comissoes.atividades.criar,
                command: () => this.emitirAcao('nova-atividade-reuniao')
            },
            {
                label: reuniao?.descricaoStatusAtaAnterior ? `Voltar: ${reuniao.descricaoStatusAtaAnterior}` : 'Status anterior',
                icon: 'pi pi-arrow-left',
                styleClass: 'bg-amber-500',
                disabled: !(podeExibirStatusAnterior && permiteAtaEStatus),
                permission: PERMISSOES.comissoes.atas.alterarStatus,
                command: () => this.emitirAcao('status-ata-anterior')
            },
            {
                label: reuniao?.descricaoStatusAtaProximo ? `Avançar: ${reuniao.descricaoStatusAtaProximo}` : 'Próximo status',
                icon: 'pi pi-arrow-right',
                styleClass: 'bg-emerald-500',
                disabled: !(podeExibirProximoStatus && permiteAtaEStatus),
                permission: PERMISSOES.comissoes.atas.alterarStatus,
                command: () => this.emitirAcao('status-ata-proximo')
            },
            {
                label: 'Reativar Reunião',
                icon: 'pi pi-refresh',
                styleClass: 'bg-surface-500',
                disabled: !permiteReativar,
                permission: PERMISSOES.comissoes.reunioes.reativar,
                command: () => this.emitirAcao('reativar-reuniao')
            }
        ];
    }

    /**
     * Menu do botão "Opções": ações gerais + ações da reunião (sempre presentes, habilitadas conforme seleção/status).
     * Sem separador — o grid de ícones renderiza cada item como uma célula, e o separador viraria um slot vazio.
     */
    protected itensOpcoesMenu(): AppMenuItem[] {
        return [...this.itensOpcoes(), ...this.itensAcoesTabelaMenu()];
    }

    private emitirAcao(acao: AcaoTabelaComissoes): void {
        this.acaoTabela.emit(acao);
    }

    executarAcaoWhatsapp(): void {
        this.sendWhatsappComponent()?.abrirWhatsapp();
    }

    gerarMensagemWhatsappReuniao(reuniao: ReuniaoComissao | null): string {
        if (!reuniao) {
            return '';
        }

        const dataStr = this.formatarData(reuniao.dataInicio);
        const statusStr = reuniao.descricaoStatusAta || reuniao.statusAta;

        const linhas = [
            '\u{1F465} *Reunião de comissão*',
            reuniao.nomeComissao ? `\u{1F3DB}\uFE0F Comissão/CT: ${reuniao.nomeComissao}` : '',
            reuniao.codigo ? `\u{1F522} Reunião: #${reuniao.codigo}` : '',
            dataStr && dataStr !== '-' ? `\u{1F4C5} Data/Hora: ${dataStr}` : '',
            reuniao.local ? `\u{1F4CD} Local: ${reuniao.local}` : '',
            statusStr ? `\u{1F4CB} Status da Ata: ${statusStr}` : ''
        ];

        return linhas.filter((linha) => linha !== '').join('\n');
    }

    private idStatusReuniao(reuniao: ReuniaoComissao | null): number | null {
        if (!reuniao) {
            return null;
        }

        if (typeof reuniao.idStatusReuniao === 'number' && Number.isFinite(reuniao.idStatusReuniao)) {
            return reuniao.idStatusReuniao;
        }

        const mapa: Record<StatusReuniaoComissao, number> = {
            agendada: 1,
            remarcada: 2,
            cancelada: 3,
            realizada: 4,
            excluida: 5
        };
        return mapa[reuniao.status] ?? null;
    }

    private statusPermiteAtaEStatus(idStatusReuniao: number | null): boolean {
        return idStatusReuniao === 1 || idStatusReuniao === 2 || idStatusReuniao === 4;
    }

    private statusPermiteNovaAtividade(idStatusReuniao: number | null): boolean {
        return idStatusReuniao === 1 || idStatusReuniao === 2;
    }

    statusRotulo(status: StatusReuniaoComissao): string {
        switch (status) {
            case 'agendada':
                return 'Agendada';
            case 'remarcada':
                return 'Remarcada';
            case 'realizada':
                return 'Realizada';
            case 'cancelada':
                return 'Cancelada';
            case 'excluida':
                return 'Excluída';
            default:
                return 'Sem status';
        }
    }

    statusSeveridade(status: StatusReuniaoComissao): 'success' | 'warn' | 'danger' | 'secondary' | 'contrast' | 'info' {
        switch (status) {
            case 'realizada':
                return 'success';
            case 'agendada':
                return 'info';
            case 'remarcada':
                return 'warn';
            case 'cancelada':
            case 'excluida':
                return 'danger';
            default:
                return 'secondary';
        }
    }

    /** Reunião sem ata vem com o campo vazio; o pictograma precisa de um nome para o tooltip. */
    statusAtaRotulo(statusAta: ReuniaoComissao['statusAta']): string {
        return statusAta || 'Sem ata';
    }

    /**
     * O status da ata chega do backend como texto livre. O mapa lê o mesmo texto normalizado que
     * `statusAtaSeveridade` lê, para ícone e cor nunca discordarem. Um rótulo fora do mapa cai no
     * ícone neutro e segue carregando o texto cru no tooltip — o dado não some da tela.
     */
    obterIconeStatusAta(statusAta: ReuniaoComissao['statusAta']): string {
        switch (this.normalizarTexto(statusAta)) {
            case 'MINUTA PENDENTE':
                return 'pi pi-file-edit';
            case 'REVISAO PENDENTE':
                return 'pi pi-search';
            case 'ASSINATURA PENDENTE':
                return 'pi pi-pencil';
            case 'CONCLUIDA':
                return 'pi pi-file-check';
            case 'PUBLICADA':
                return 'pi pi-megaphone';
            default:
                return 'pi pi-file';
        }
    }

    /**
     * A razão confirmadas/convocadas diz mais que os dois números soltos. Sem convocação a razão não
     * existe — "12/0" leria como dado corrompido —, então sobra só quantas confirmaram.
     */
    protected rotuloParticipacao(item: ReuniaoRowItem): string {
        const convocadas = Number(item.convocacoes);
        return convocadas > 0 ? `${item.confirmacoes} confirmações de ${item.convocacoes} convocações` : `${item.confirmacoes} confirmações`;
    }

    /** A mesma razão, compacta, para a célula: "12/30" — ou só "12" sem convocação. */
    protected razaoParticipacao(item: ReuniaoRowItem): string {
        return Number(item.convocacoes) > 0 ? `${item.confirmacoes}/${item.convocacoes}` : item.confirmacoes;
    }

    /** Código e nome juntos ("118 - Câmara Técnica…"), como a comissão é citada nas outras telas. */
    protected tituloComissao(item: ReuniaoRowItem): string {
        const nome = item.nomeComissao || '-';
        return item.codigoComissao ? `${item.codigoComissao} - ${nome}` : nome;
    }

    /** Só o dia de um "dd/MM/yyyy HH:mm" já formatado — a hora desce para a linha de baixo, menor. */
    protected diaDe(valor: string): string {
        return valor === '-' ? '-' : valor.split(' ')[0];
    }

    /** Só a hora do mesmo formato; vazio quando o valor não tem hora. */
    protected horaDe(valor: string): string {
        return valor === '-' ? '' : valor.split(' ')[1] || '';
    }

    /** Modalidade e local para o subtítulo do painel expandido — só o local quando não há modalidade. */
    protected subtituloExpandido(item: ReuniaoRowItem): string {
        return this.exibeModalidade(item) ? `${item.modalidade.value} · ${item.local.value}` : item.local.value;
    }

    /** O selo da modalidade só aparece quando há modalidade — "Não Informada" não é dado, é ausência. */
    protected exibeModalidade(item: ReuniaoRowItem): boolean {
        const normalizada = this.normalizarTexto(item.modalidade.value);
        return Boolean(normalizada) && normalizada !== 'NAO INFORMADA' && normalizada !== '-';
    }

    /** Iniciais para o avatar do assistente — duas primeiras palavras, como nos avatares da casa. */
    protected iniciaisAssistente(nome: string): string {
        const limpo = (nome || '').trim();
        if (!limpo || limpo === '-') return '';
        return limpo
            .split(/\s+/)
            .slice(0, 2)
            .map((parte) => parte[0])
            .join('')
            .toUpperCase();
    }

    /**
     * Faixa de horário: "10:22 - 12:50". Quando o término cai noutro dia — reunião que vira a
     * noite —, o dia dele vai entre parênteses para a faixa não mentir.
     */
    protected faixaHorario(item: ReuniaoRowItem): string {
        const [diaInicio, horaInicio] = item.dataInicio.split(' ');
        if (item.dataInicio === '-' || !horaInicio) return '';

        const [diaTermino, horaTermino] = (item.dataTermino || '-').split(' ');
        if (!horaTermino) return horaInicio;

        return diaTermino === diaInicio ? `${horaInicio} - ${horaTermino}` : `${horaInicio} - ${horaTermino} (${diaTermino})`;
    }

    /** Uma cor por modalidade, no selo da coluna da comissão: verde=presencial, azul=híbrida, âmbar=remota. */
    modalidadeSeveridade(modalidade: ReuniaoComissao['modalidade']): 'success' | 'warn' | 'danger' | 'secondary' | 'contrast' | 'info' {
        const modalidadeNormalizada = this.normalizarTexto(modalidade);

        switch (modalidadeNormalizada) {
            case 'HIBRIDA':
                return 'info';
            case 'PRESENCIAL':
                return 'success';
            case 'VIRTUAL':
            case 'REMOTA':
            case 'VIDEOCONFERENCIA':
                return 'warn';
            case 'NAO INFORMADA':
                return 'secondary';
            default:
                return 'secondary';
        }
    }

    statusAtaSeveridade(statusAta: ReuniaoComissao['statusAta']): 'success' | 'warn' | 'danger' | 'secondary' | 'contrast' | 'info' {
        const statusNormalizado = this.normalizarTexto(statusAta);

        switch (statusNormalizado) {
            case 'CONCLUIDA':
                return 'success';
            case 'PUBLICADA':
                return 'info';
            case 'ASSINATURA PENDENTE':
                return 'warn';
            case 'MINUTA PENDENTE':
                return 'warn';
            case 'REVISAO PENDENTE':
                return 'danger';
            default:
                return 'secondary';
        }
    }

    localSeveridade(local: string): 'success' | 'warn' | 'danger' | 'secondary' | 'contrast' | 'info' {
        const localNormalizado = this.normalizarTexto(local);

        if (localNormalizado.includes('VIDEOCONFERENCIA')) {
            return 'info';
        }
        if (localNormalizado.includes('SEDE DO CFM')) {
            return 'success';
        }
        return 'secondary';
    }

    totalRegistrosLabel(): string {
        const total = this.totalRegistros() ?? this.reunioes().length;
        const sufixo = total === 1 ? 'registro' : 'registros';
        return `Total de ${sufixo}: ${total}`;
    }

    private normalizarTexto(valor: string): string {
        return (valor || '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .trim()
            .toUpperCase();
    }

    isRowSelected(rowData: ReuniaoRowItem): boolean {
        return Number(this.reuniaoSelecionada()?.codigo) === rowData.codigo;
    }

    obterDataHoraFormatada(item: ReuniaoRowItem): string {
        if (!item.dataInicio || item.dataInicio === '-') return '-';
        const partesInicio = item.dataInicio.split(' ');
        const data = partesInicio[0];
        const horaInicio = partesInicio[1] || '';

        let horaFim = '';
        if (item.dataTermino && item.dataTermino !== '-') {
            const partesTermino = item.dataTermino.split(' ');
            horaFim = partesTermino[1] || '';
        }

        if (horaInicio && horaFim) {
            return `${data} • ${horaInicio} - ${horaFim}`;
        } else if (horaInicio) {
            return `${data} • ${horaInicio}`;
        }
        return data;
    }

    obterIconeStatus(status: string): string {
        switch (status) {
            case 'Realizada':
                return 'pi pi-check-circle';
            case 'Agendada':
                return 'pi pi-calendar';
            case 'Remarcada':
                return 'pi pi-refresh';
            case 'Cancelada':
                return 'pi pi-ban';
            case 'Excluída':
                return 'pi pi-trash';
            default:
                return 'pi pi-info-circle';
        }
    }

    /** Tom de cada indicador, exposto para o template não carregar string solta. */
    protected readonly ETomKpi = ETomKpi;

    /**
     * Valor do cartão de data: só a data e a hora. O código da reunião saiu daqui e foi para a
     * legenda — no cartão antigo os dois dividiam a mesma linha e nenhum dos dois lia bem.
     */
    protected valorDataReuniao(data?: string | null): string {
        return data ? this.formatarData(data) : SEM_DADO_KPI;
    }

    /**
     * Legenda do cartão de data: havendo data, o código que identifica a reunião; não havendo, o
     * motivo da ausência — que é o que falta saber a quem está olhando para um traço.
     */
    protected legendaDataReuniao(data: string | null | undefined, codigo: number | null | undefined, ausencia: string): string {
        if (!data) {
            return ausencia;
        }
        return codigo ? `Cód. ${codigo}` : '';
    }

    /** Valor do cartão de agendadas já como texto: o cartão recebe texto pronto, não número. */
    protected valorAgendadas(total?: number | null): string {
        return String(total ?? 0);
    }

    /** Sem nenhuma agendada, dizer isso vale mais do que repetir o zero por extenso. */
    protected legendaAgendadas(total?: number | null): string {
        return (total ?? 0) === 0 ? 'Nenhuma reunião futura' : 'Aguardando realização';
    }
}
